import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi } from "@/utils/adminApi";
import { ArrowLeft, Save, Upload, X, Plus, Trash2, GripVertical } from "lucide-react";
import { useTourGuide, HelpButton } from "@/components/TourGuide";

const CATEGORIES = ["Experience the Day", "Experience the Hike", "Taylor Made", "Farther Afield", "Two Day Tours", "Multi-Day International"];
const PRICE_TYPES = [{ value: "tiered", label: "Tiered" }, { value: "call", label: "Call for Prices" }, { value: "fixed", label: "Fixed" }];
const DEFAULT_TOUR = {
  title: "", slug: "", subtitle: "", category: "Experience the Day", description: "",
  duration: "Full Day", price: 0, price_type: "tiered", price_tiers: [], price_note: "Minimum 2 guests",
  currency: "USD", image_url: "", gallery: [], highlights: [], itinerary: [],
  includes: ["Private transport", "Bilingual expert guide", "Gourmet lunch", "All entrance fees", "Bottled water & refreshments"],
  max_guests: 8
};

function Section({ title, children }) {
  return (
    <div className="border border-white/10 p-6 space-y-4">
      <h3 className="font-sans text-[#C5A059] text-xs tracking-[0.15em] uppercase">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, textarea, required }) {
  const cls = "w-full bg-[#0A0A0A] border border-white/10 focus:border-[#C5A059] text-white font-sans text-sm p-3 outline-none transition-colors placeholder:text-white/20";
  return (
    <div>
      {label && <label className="font-sans text-white/60 text-xs mb-1 block">{label}</label>}
      {textarea
        ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} required={required} className={`${cls} resize-y`} />
        : <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} className={cls} />
      }
    </div>
  );
}

function EditableList({ items, onUpdate, onRemove, onAdd, addLabel, placeholder }) {
  return (
    <>
      {(items || []).map((item, i) => (
        <div key={`item-${i}`} className="flex items-center gap-2">
          <input value={item} onChange={e => onUpdate(i, e.target.value)} placeholder={placeholder}
            className="flex-1 bg-[#0A0A0A] border border-white/10 text-white font-sans text-sm p-2 outline-none" />
          <button onClick={() => onRemove(i)} className="text-red-400/50 hover:text-red-400"><X size={14} /></button>
        </div>
      ))}
      <button onClick={onAdd} className="text-[#C5A059] font-sans text-xs flex items-center gap-1"><Plus size={12} /> {addLabel}</button>
    </>
  );
}

function CoverSection({ imageUrl, onUrlChange, onUpload, uploading }) {
  const coverRef = useRef(null);
  return (
    <Section title="Cover Image">
      <div className="flex gap-4 items-start">
        {imageUrl && <img src={imageUrl} alt="Cover" className="w-40 h-28 object-cover border border-white/10" />}
        <div className="flex-1 space-y-2">
          <Field label="Image URL" value={imageUrl} onChange={onUrlChange} placeholder="Paste URL or upload" />
          <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && onUpload(e.target.files[0])} />
          <button onClick={() => coverRef.current?.click()} disabled={uploading}
            className="flex items-center gap-2 border border-white/10 text-white/60 font-sans text-xs px-4 py-2 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors disabled:opacity-30">
            <Upload size={12} /> {uploading ? "Uploading..." : "Upload Image"}
          </button>
        </div>
      </div>
    </Section>
  );
}

function GallerySection({ images, onRemove, onUpload, onAddUrl, uploading }) {
  const fileRef = useRef(null);
  return (
    <Section title={`Gallery (${images?.length || 0} images)`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {(images || []).map((img, i) => (
          <div key={`gallery-${i}-${img.slice(-20)}`} className="relative group aspect-square">
            <img src={img} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onRemove(i)}
              className="absolute top-1 right-1 bg-black/70 p-1 opacity-0 group-hover:opacity-100 transition-opacity text-red-400">
              <X size={12} />
            </button>
          </div>
        ))}
        <div className="aspect-square border border-dashed border-white/10 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-[#C5A059]/50 transition-colors"
          onClick={() => fileRef.current?.click()}>
          <Plus size={20} className="text-white/20" />
          <span className="text-white/20 text-xs">Add Photo</span>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && onUpload(e.target.files[0])} />
      <Field label="Or paste image URL" value="" onChange={v => { if (v) onAddUrl(v); }} placeholder="https://..." />
    </Section>
  );
}

function PricingSection({ priceType, price, tiers, note, onUpdate }) {
  const updateTier = (idx, field, val) => {
    const updated = [...(tiers || [])];
    updated[idx] = { ...updated[idx], [field]: field === "price" ? parseFloat(val) || 0 : val };
    onUpdate("price_tiers", updated);
  };

  return (
    <Section title="Pricing">
      <div className="flex gap-3 items-center mb-4">
        {PRICE_TYPES.map(pt => (
          <button key={pt.value} onClick={() => onUpdate("price_type", pt.value)}
            className={`font-sans text-xs px-4 py-2 border transition-colors ${priceType === pt.value ? "border-[#C5A059] text-[#C5A059]" : "border-white/10 text-white/40"}`}>
            {pt.label}
          </button>
        ))}
      </div>
      {priceType === "tiered" && (
        <div className="space-y-2">
          {(tiers || []).map((tier, i) => (
            <div key={`tier-${i}`} className="flex gap-3 items-center">
              <input value={tier.label} onChange={e => updateTier(i, "label", e.target.value)} placeholder="e.g. 2-4 guests"
                className="flex-1 bg-[#0A0A0A] border border-white/10 text-white font-sans text-sm p-2 outline-none" />
              <div className="flex items-center gap-1">
                <span className="text-[#C5A059]">$</span>
                <input type="number" value={tier.price} onChange={e => updateTier(i, "price", e.target.value)}
                  className="w-20 bg-[#0A0A0A] border border-white/10 text-white font-sans text-sm p-2 outline-none" />
              </div>
              <button onClick={() => onUpdate("price_tiers", tiers.filter((_, j) => j !== i))} className="text-red-400/50 hover:text-red-400"><X size={14} /></button>
            </div>
          ))}
          <button onClick={() => onUpdate("price_tiers", [...(tiers || []), { label: "", price: 0 }])}
            className="text-[#C5A059] font-sans text-xs flex items-center gap-1"><Plus size={12} /> Add Tier</button>
          <Field label="Price Note" value={note || ""} onChange={v => onUpdate("price_note", v)} placeholder="e.g. Minimum 2 guests" />
        </div>
      )}
      {priceType === "fixed" && <Field label="Price per person ($)" value={price} onChange={v => onUpdate("price", parseFloat(v) || 0)} type="number" />}
      {priceType === "call" && <Field label="Note" value={note || ""} onChange={v => onUpdate("price_note", v)} placeholder="e.g. Season available" />}
    </Section>
  );
}

function ItinerarySection({ items, onUpdate, onRemove, onAdd }) {
  return (
    <Section title={`Itinerary (${items?.length || 0} stops)`}>
      <div className="space-y-3">
        {(items || []).map((stop, i) => (
          <div key={`stop-${i}`} className="border border-white/5 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <GripVertical size={14} className="text-white/20" />
              <input value={stop.title} onChange={e => onUpdate(i, "title", e.target.value)} placeholder="Stop title"
                className="flex-1 bg-transparent border-b border-white/10 text-white font-sans text-sm py-1 outline-none placeholder:text-white/20 focus:border-[#C5A059]" />
              <button onClick={() => onRemove(i)} className="text-red-400/50 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
            <textarea value={stop.description} onChange={e => onUpdate(i, "description", e.target.value)} placeholder="Description..."
              rows={2} className="w-full bg-[#0A0A0A] border border-white/5 text-white/70 font-sans text-xs p-2 outline-none resize-y placeholder:text-white/15" />
          </div>
        ))}
      </div>
      <button onClick={onAdd} className="text-[#C5A059] font-sans text-xs flex items-center gap-1"><Plus size={12} /> Add Stop</button>
    </Section>
  );
}

export default function AdminTourEditor() {
  const { tourId } = useParams();
  const nav = useNavigate();
  const isNew = tourId === "new";

  const [tour, setTour] = useState({ ...DEFAULT_TOUR });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const { GuideComponent, restart } = useTourGuide("editor");

  useEffect(() => {
    if (!isNew) {
      adminApi.get(`/api/admin/tours/${tourId}`)
        .then(r => setTour(r.data))
        .catch(() => nav("/admin/dashboard"));
    }
  }, [tourId, isNew, nav]);

  const update = (key, val) => setTour(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      if (isNew) {
        const { data } = await adminApi.post("/api/admin/tours", tour);
        setMsg("Tour created!");
        setTimeout(() => nav(`/admin/tours/${data.id}`), 1000);
      } else {
        await adminApi.put(`/api/admin/tours/${tourId}`, tour);
        setMsg("Saved!");
      }
    } catch (err) {
      setMsg("Error: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const uploadImage = async (file, target) => {
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await adminApi.post("/api/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      // Use the relative URL returned by the backend (e.g. /api/images/abc123).
      // It resolves to the current host in both preview and production, and
      // survives any future env-var changes.
      const fullUrl = data.url;
      if (target === "cover") {
        update("image_url", fullUrl);
      } else {
        update("gallery", [...(tour.gallery || []), fullUrl]);
      }
    } catch (err) {
      setMsg("Upload failed: " + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  const updateListItem = (key, idx, val) => {
    const updated = [...(tour[key] || [])];
    updated[idx] = val;
    update(key, updated);
  };

  const updateItinerary = (idx, field, val) => {
    const updated = [...(tour.itinerary || [])];
    updated[idx] = { ...updated[idx], [field]: val };
    update("itinerary", updated);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {GuideComponent}
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 bg-[#050505] z-10">
        <div className="flex items-center gap-4">
          <button onClick={() => nav("/admin/dashboard")} className="text-white/40 hover:text-white"><ArrowLeft size={18} /></button>
          <div>
            <h1 className="font-serif text-lg">{isNew ? "New Tour" : "Edit Tour"}</h1>
            <p className="font-sans text-white/40 text-xs">{tour.title || "Untitled"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {msg && <span className={`font-sans text-xs ${msg.startsWith("Error") ? "text-red-400" : "text-green-400"}`}>{msg}</span>}
          <HelpButton onClick={restart} />
          <button onClick={save} disabled={saving} data-testid="save-tour-btn"
            className="flex items-center gap-2 bg-[#C5A059] text-black font-sans text-xs tracking-wider uppercase px-5 py-2 hover:bg-[#A8853D] transition-colors disabled:opacity-30">
            <Save size={14} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <Section title="Basic Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title" value={tour.title} onChange={v => update("title", v)} required />
            <Field label="Slug (URL)" value={tour.slug} onChange={v => update("slug", v)} placeholder="e.g. my-tour-name" required />
            <Field label="Subtitle" value={tour.subtitle} onChange={v => update("subtitle", v)} />
            <div>
              <label className="font-sans text-white/60 text-xs mb-1 block">Category</label>
              <select value={tour.category} onChange={e => update("category", e.target.value)}
                className="w-full bg-[#0A0A0A] border border-white/10 text-white font-sans text-sm p-3 outline-none">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Duration" value={tour.duration} onChange={v => update("duration", v)} />
            <Field label="Max Guests" value={tour.max_guests} onChange={v => update("max_guests", parseInt(v) || 1)} type="number" />
          </div>
          <Field label="Description" value={tour.description} onChange={v => update("description", v)} textarea />
        </Section>

        <CoverSection imageUrl={tour.image_url} onUrlChange={v => update("image_url", v)}
          onUpload={f => uploadImage(f, "cover")} uploading={uploading} />

        <GallerySection images={tour.gallery} onRemove={i => update("gallery", (tour.gallery || []).filter((_, j) => j !== i))}
          onUpload={f => uploadImage(f, "gallery")} onAddUrl={v => update("gallery", [...(tour.gallery || []), v])} uploading={uploading} />

        <PricingSection priceType={tour.price_type} price={tour.price} tiers={tour.price_tiers}
          note={tour.price_note} onUpdate={update} />

        <ItinerarySection items={tour.itinerary}
          onUpdate={updateItinerary}
          onRemove={i => update("itinerary", (tour.itinerary || []).filter((_, j) => j !== i))}
          onAdd={() => update("itinerary", [...(tour.itinerary || []), { day: (tour.itinerary?.length || 0) + 1, title: "", description: "" }])} />

        <Section title={`Highlights (${tour.highlights?.length || 0})`}>
          <EditableList items={tour.highlights}
            onUpdate={(i, v) => updateListItem("highlights", i, v)}
            onRemove={i => update("highlights", (tour.highlights || []).filter((_, j) => j !== i))}
            onAdd={() => update("highlights", [...(tour.highlights || []), ""])}
            addLabel="Add Highlight" placeholder="Highlight text" />
        </Section>

        <Section title={`Includes (${tour.includes?.length || 0})`}>
          <EditableList items={tour.includes}
            onUpdate={(i, v) => updateListItem("includes", i, v)}
            onRemove={i => update("includes", (tour.includes || []).filter((_, j) => j !== i))}
            onAdd={() => update("includes", [...(tour.includes || []), ""])}
            addLabel="Add Item" placeholder="Include item" />
        </Section>
      </div>
    </div>
  );
}
